// Bootstrap, adopted into shadow roots via constructable stylesheets.
//
// Components use Shadow DOM, which the CDN <link> in index.html cannot reach
// into — so a component that just writes Bootstrap class names gets nothing.
// This fetches the same stylesheet once, builds one CSSStyleSheet, and shares it
// across every shadow root via adoptedStyleSheets. One fetch, one sheet object,
// however many components.
//
// The alternative — unsafeCSS(bootstrapText) in each component's static styles —
// would give every component its own copy of a ~200KB sheet. This is the pattern
// d3-kit (spa/styles.js) and d3-auth (web/lib/styles.js) both already use.

// These MUST match the versions in index.html: the <link> there exists to warm
// the HTTP cache for these exact fetches, so a mismatch silently downloads
// Bootstrap twice.
const BOOTSTRAP_CSS_URL = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css';
const BOOTSTRAP_ICONS_CSS_URL =
    'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css';

/** @type {Promise<CSSStyleSheet[]>|null} */
let loadingPromise = null;

async function fetchSheet(url) {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to load stylesheet ${url}: ${response.status}`);
    }

    const sheet = new CSSStyleSheet();
    sheet.replaceSync(await response.text());
    return sheet;
}

/**
 * Load the shared Bootstrap stylesheets. The module-level promise means the
 * fetch happens exactly once no matter how many components ask concurrently.
 * @returns {Promise<CSSStyleSheet[]>}
 */
export function getSharedStyles() {
    if (!loadingPromise) {
        loadingPromise = Promise.all([
            fetchSheet(BOOTSTRAP_CSS_URL),
            fetchSheet(BOOTSTRAP_ICONS_CSS_URL),
        ]);
    }
    return loadingPromise;
}
